/**
 * narrative-generation page content — 100% fidelity to content
 * Cross-reference: source .md file
 */

export const narrativeGenerationMeta = {
  title: "Narrative Generation Engine | Perception X2 AI Content Creation",
  description: "Transformer-based NLG engine generating 1,000-100,000+ content variants per campaign. 50+ templates, 15+ languages, 144-dimensional emotional mapping. Power Perception X2.",
  slug: "engines/narrative-generation",
  canonical: "https://perception.ac/engines/narrative-generation",
};

export interface HeroData {
  eyebrow: string;
  title: string;
  titleAccent: string;
  lede: string;
  ctaPrimary: { label: string; href: string };
  ctaSecondary: { label: string; href: string };
  trustIndicators: string[];
}

export const hero: HeroData = {
  eyebrow: "Narrative Generation",
  title: "Narrative Generation Engine",
  titleAccent: "Engineered for Excellence",
  lede: "Transformer-based NLG engine generating 1,000-100,000+ content variants per campaign. 50+ templates, 15+ languages, 144-dimensional emotional mapping. Power Perception X2.",
  ctaPrimary: { label: "Request a Consultation", href: "#contact" },
  ctaSecondary: { label: "Explore the Platform", href: "/platform/overview" },
  trustIndicators: ["15+ Years", "300+ Clients", "50+ Platforms", "Zero Incidents"],
};

export interface ExecutiveSummaryData {
  eyebrow: string;
  title: string;
  paragraphs: string[];
  cta: { label: string; href: string };
}

export const executiveSummary: ExecutiveSummaryData = {
  eyebrow: "Executive Summary",
  title: "Understanding the Narrative Generation Advantage",
  paragraphs: [
    "Transformer-based NLG engine generating 1,000-100,000+ content variants per campaign. 50+ templates, 15+ languages, 144-dimensional emotional mapping. Power Perception X2.",
    "Perception X2 is the autonomous perception amplification platform built by CryptoMize — operating through 15+ years of refined engineering, 300+ elite client engagements, and nine proprietary AI platforms. Every capability has been forged in the most demanding environments on earth.",
    "This page details how Perception X2 delivers outcomes that conventional approaches cannot match. Each section reflects measured, verified performance — not marketing claims.",
  ],
  cta: { label: "Explore the Details", href: "#details" },
};

export interface ProblemData {
  eyebrow: string;
  title: string;
  pillars: { title: string; body: string; icon: string }[];
}

export const problem: ProblemData = {
  eyebrow: "The Problem",
  title: "The Structural Challenges Organizations Face",
  pillars: [
    { title: "The Velocity Gap", body: "Digital narratives move at machine speed. Traditional approaches operate on weekly or monthly cycles. By the time responses are drafted, the narrative has already crystallized in public consciousness.", icon: "Clock" },
    { title: "The Authenticity Deficit", body: "Generic content strategies produce detectable, inauthentic output. Audiences recognize manufactured messaging and disengage. Conventional approaches achieve authenticity ratings below 70%.", icon: "Fingerprint" },
    { title: "The Scale Barrier", body: "Managing perception across 50+ platforms, 15+ languages, and multiple audience segments requires coordination that exceeds human cognitive capacity.", icon: "Maximize" },
    { title: "The Integration Gap", body: "Disconnected tools produce data silos, manual handoffs, inconsistent messaging, and compounding inefficiencies. The cost of fragmentation multiplies with every touchpoint.", icon: "Layers" },
  ],
};

export interface SolutionData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string };
}

export const solution: SolutionData = {
  eyebrow: "The Solution",
  title: "How Perception X2 Solves These Challenges",
  intro: "Perception X2 addresses each structural challenge through autonomous, AI-driven capabilities operating as a unified system — replacing fragmented manual approaches with intelligent, coordinated execution.",
  pillars: [
    { title: "Autonomous Execution", body: "Perception X2 operates independently — scanning, analyzing, generating, deploying, and optimizing across 50+ digital platforms with minimal human intervention. Human operators set strategic objectives. The platform executes." },
    { title: "Integrated Intelligence", body: "Four proprietary AI engines work as a unified pipeline. Insights from one engine inform the others. The output of each is the input of the next. This integration creates compounding intelligence that standalone tools cannot achieve." },
    { title: "Verified Performance", body: "300-500% narrative penetration improvement. 95-98% organic authenticity. 99.9999% uptime. 384x-1,416x faster crisis response. These are not theoretical maximums — they are sustained production averages across 300+ engagements." },
  ],
  cta: { label: "Explore the Details", href: "#details" },
};

export interface BenefitsData {
  eyebrow: string;
  title: string;
  benefits: { title: string; body: string; icon: string; accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" }[];
}

export const benefits: BenefitsData = {
  eyebrow: "Benefits",
  title: "The Perception X2 Advantage",
  benefits: [
    { title: "95-98% Organic Authenticity", body: "Content produced across all capabilities maintains authenticity ratings statistically indistinguishable from organic engagement — strengthening brand credibility across all platforms.", icon: "Fingerprint", accent: "crimson" },
    { title: "300-500% Narrative Penetration", body: "Consistently achieved across diverse industries and engagement scales — the measurable difference between organic reach and Perception X2-amplified reach.", icon: "TrendingUp", accent: "emerald" },
    { title: "Sub-Hour Activation", body: "From strategy approval to full deployment across 50+ platforms in under one hour. Integrated capabilities eliminate the weeks-long timelines of fragmented approaches.", icon: "Zap", accent: "amber" },
    { title: "Autonomous 24/7 Operation", body: "The platform operates autonomously while maintaining human oversight at critical decision points. Your team focuses on strategy while the platform handles execution.", icon: "Cpu", accent: "cyan" },
    { title: "Multi-Platform Coverage", body: "50+ digital platforms with 15+ language support across 85+ regions. Your narratives reach every audience segment on every platform that matters.", icon: "Globe", accent: "purple" },
    { title: "Zero Security Incidents", body: "15+ years. 300+ elite clients. Zero security incidents. Your perception data protected by standards that exceed enterprise requirements.", icon: "Shield", accent: "denim" },
    { title: "Crisis Response Speed", body: "Pre-built crisis response playbooks activate within minutes. Threat containment and counter-narrative deployment at speeds 384x to 1,416x faster than traditional approaches.", icon: "Siren", accent: "crimson" },
    { title: "Continuous Optimization", body: "Real-time A/B testing, sentiment tracking, and engagement analysis feed performance data back through all engines. The system improves with every iteration.", icon: "Activity", accent: "violet" },
  ],
};

export interface UspsData {
  eyebrow: string;
  title: string;
  intro: string;
  usps: { title: string; body: string }[];
  cta: { label: string; href: string };
}

export const usps: UspsData = {
  eyebrow: "Our USPs",
  title: "What Makes Perception X2 Different",
  intro: "No other perception management platform delivers these capabilities within a unified autonomous system.",
  usps: [
    { title: "Unified Autonomous Platform", body: "No other platform delivers these capabilities within a unified system. Competitors offer fragments. Perception X2 delivers the complete stack as one autonomous platform." },
    { title: "15+ Years of Operational Refinement", body: "Every capability refined through thousands of real-world deployments across the world's most demanding environments. Not a startup with a prototype — an operational platform with a track record." },
    { title: "Nine Proprietary AI Platforms", body: "Not licensed. Not white-labeled. Nine AI platforms — each purpose-built and operationally proven through 15+ years of continuous development." },
    { title: "Verified Performance Metrics", body: "300-500% penetration. 95-98% authenticity. 384x-1,416x faster crisis response. Verified across 300+ elite engagements." },
    { title: "Zero Third-Party Dependencies", body: "Every capability runs on proprietary infrastructure. Zero reliance on third-party cloud services, licensed APIs, or external data providers." },
    { title: "Zero Security Incidents", body: "15+ years. 300+ elite clients. Zero security incidents. Your perception data protected by standards that exceed enterprise requirements." },
  ],
  cta: { label: "View Platform Overview", href: "/platform/overview" },
};

export interface WhoItsForData {
  eyebrow: string;
  title: string;
  segments: { title: string; body: string; icon: string; accent: "crimson" | "denim" | "violet" | "amber" | "emerald" | "purple" | "cyan" }[];
}

export const whoItsFor: WhoItsForData = {
  eyebrow: "Who It's For",
  title: "Organizations That Benefit from Perception X2",
  segments: [
    { title: "Government and Sovereign Entities", body: "National narrative management, public trust campaigns, diplomatic communication, and institutional reputation defense.", icon: "Landmark", accent: "crimson" },
    { title: "Global Enterprises", body: "Brand reputation across multiple markets, competitive positioning, product launch perception, and executive reputation management.", icon: "Building2", accent: "denim" },
    { title: "Political Organizations and Campaigns", body: "Campaign narrative deployment, voter sentiment management, opposition research response, and public discourse shaping.", icon: "Vote", accent: "violet" },
    { title: "Public Figures and HNWIs", body: "Personal brand protection, privacy management, legacy curation, and crisis prevention with the discretion high-profile individuals require.", icon: "User", accent: "amber" },
    { title: "Financial Institutions", body: "Market perception management, regulatory positioning, investor relations narrative, and crisis prevention where trust is the primary asset.", icon: "TrendingUp", accent: "emerald" },
    { title: "Defense and Strategic Organizations", body: "Strategic communication, threat assessment, and classified project perception management at security standards defense environments require.", icon: "Shield", accent: "purple" },
  ],
};

export interface FAQItem { q: string; a: string }
export interface FAQData { eyebrow: string; title: string; items: FAQItem[] }

export const faq: FAQData = {
  eyebrow: "Frequently Asked Questions",
  title: "Common Questions",
  items: [
    { q: "What is Perception X2?", a: "Perception X2 is the autonomous perception amplification platform built by CryptoMize. It operates through four proprietary AI engines working as an integrated pipeline — delivering 300-500% narrative penetration improvement with 95-98% organic authenticity across 50+ digital platforms." },
    { q: "How does Perception X2 differ from traditional approaches?", a: "Traditional approaches rely on human teams manually monitoring platforms and creating content — operating on weekly cycles across 5-10 platforms. Perception X2 replaces this with autonomous AI-driven execution that operates in hours across 50+ platforms simultaneously." },
    { q: "What are the verified performance metrics?", a: "Perception X2 delivers 300-500% narrative penetration improvement, 95-98% organic authenticity ratings, 99.9999% platform uptime, 384x-1,416x faster crisis response, and 78-92% trajectory prediction accuracy." },
    { q: "How quickly can Perception X2 be activated?", a: "From strategy approval to full deployment across 50+ platforms in under one hour. Complex enterprise deployments with custom integrations complete within 4-6 weeks." },
    { q: "What industries does Perception X2 serve?", a: "Perception X2 serves organizations where perception directly determines outcomes: government, global enterprises, political organizations, public figures, defense organizations, financial institutions, educational and healthcare institutions, and international organizations." },
    { q: "Is Perception X2 secure?", a: "Perception X2 operates on sovereign infrastructure with AES-256 encryption, TLS 1.3, zero-trust architecture, SOC 2 Type II, and ISO 27001 certification. Zero security incidents across 15+ years of operations." },
    { q: "Does Perception X2 support multiple languages?", a: "Yes. Perception X2 operates across 15+ languages and 85+ regions with cultural context awareness. The cultural localization capability produces language-native output, not translated content." },
    { q: "What deployment tiers are available?", a: "Perception X2 offers four deployment tiers: Stealth (100-1,000 personas), Moderate (1,000-10,000), Aggressive (10,000-100,000), and Maximum (100,000-1,000,000+ personas)." },
    { q: "How does the feedback loop improve performance?", a: "Deployment performance data feeds back through all four engines simultaneously. The OSE updates propagation models, the CRE refines emotional mapping, the NGE adjusts content parameters. This creates a self-improving cycle." },
    { q: "How do I get started?", a: "Contact our team for a confidential consultation. We begin with a comprehensive perception audit — mapping your current digital landscape and developing a tailored engagement plan." },
  ],
};

export interface CtaData {
  eyebrow: string;
  title: string;
  intro: string;
  ctaPrimary: { label: string; href: string };
  ctaSecondary: { label: string; href: string };
  trustBar: string[];
}

export const cta: CtaData = {
  eyebrow: "Get Started",
  title: "Command Your Perception. Autonomously.",
  intro: "Every day without active perception management is a day your narrative is defined by others. Perception X2 gives you the autonomous capability to shape, protect, and amplify your perception at scale.",
  ctaPrimary: { label: "Request a Consultation", href: "#contact" },
  ctaSecondary: { label: "Explore the Platform", href: "/platform/overview" },
  trustBar: ["15+ Years", "300+ Clients", "50+ Platforms", "95-98% Authenticity", "99.9999% Uptime"],
};
